import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import DocumentPreview from '../../utils/DocumentPreview';
import TrimContent from '../../utils/TrimContent';
import ReadMoreTruncateText from '../../utils/ReadMoreTruncateText';
import PaginationControls from "../../utils/PaginationControls";
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const ValueMessageSelector = ({ 
  isOpen, 
  onClose, 
  templateId, 
  fabricId, 
  fabricName,
  currentSectionForVM,
  sectionsDict,
  onSelectionSave
}) => {
  const [selectedVMIds, setSelectedVMIds] = useState([]);
  const [valueMessages, setValueMessages] = useState([]);
  const [vmLoading, setVmLoading] = useState(false);
  const [vmSearch, setVmSearch] = useState("");
  const [vmSearchInput, setVmSearchInput] = useState("");
  const [vmPage, setVmPage] = useState(1);
  const [vmSize, setVmSize] = useState(25);
  const [vmCategory, setVmCategory] = useState("");
  const [vmAddedBy, setVmAddedBy] = useState("");
  const [categories, setCategories] = useState([]);
  const [addedByOptions] = useState(["HUMAN", "AI"]);
  const [expandedDocuments, setExpandedDocuments] = useState({});
  
  // Document preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);

  const navigate = useNavigate();

  const API_BASE_URL = '/pceapi/genbi';
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Initialize selected VMs when modal opens
  useEffect(() => {
    if (isOpen && currentSectionForVM) {
      const section = sectionsDict[currentSectionForVM];
      setSelectedVMIds(section?.vm_mappings || []);
      // Reset filters and pagination when modal opens
      setVmCategory("");
      setVmAddedBy("");
      setVmPage(1);
      setVmSearch("");
      setVmSearchInput("");
      fetchValueMessages();
    }
  }, [isOpen, currentSectionForVM]);

  // Update categories when value messages change
  useEffect(() => {
    const uniqueCategories = [
      ...new Set((valueMessages || []).map((vm) => vm.type_name).filter(Boolean)),
    ];
    setCategories(uniqueCategories);
  }, [valueMessages]);

  // Fetch value messages for VM selection
  const fetchValueMessages = async () => {
    if (vmLoading) return;
    setVmLoading(true);
    try {
      const requestBody = {
        template_id: templateId,
        fabric_id: fabricId,
        page: 1, // Always fetch all for client-side filtering
        size: 1000 // Get a large number to handle client-side pagination
      };

      // Add filters if they exist (though we're doing client-side filtering)
      if (vmCategory) {
        requestBody.category = vmCategory;
      }
      if (vmAddedBy) {
        requestBody.added_by = String(vmAddedBy).toUpperCase();
      }

      const response = await axios.post(
        `${API_BASE_URL}/value-message/list-value-messages`,
        requestBody,
        { headers }
      );

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.status === 'failed') {
        console.error(response.data.message || 'Failed to load value messages');
        return;
      }

      setValueMessages(response.data.data || []);
    } catch (error) {
      console.error('Error fetching value messages:', error);
    } finally {
      setVmLoading(false);
    }
  };

  const handleVmSearchInputChange = (e) => {
    setVmSearchInput(e.target.value);
  };

  const handleVmKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleVmSearch();
    }
  };

  const handleVmSearch = () => {
    setVmSearch(vmSearchInput);
    setVmPage(1); // Reset to first page when searching
  };

  const handleVMToggle = (vmId) => {
    setSelectedVMIds(prev => 
      prev.includes(vmId) 
        ? prev.filter(id => id !== vmId)
        : [...prev, vmId]
    );
  };

  const handleSaveSelection = () => {
    if (currentSectionForVM) {
      onSelectionSave(currentSectionForVM, selectedVMIds);
    }
    onClose();
  };

  const selectAllVMs = () => {
    const filteredVMs = getFilteredValueMessages();
    const allFilteredVMIds = filteredVMs.map(vm => vm.id);
    setSelectedVMIds(prev => [...new Set([...prev, ...allFilteredVMIds])]);
  };

  const deselectAllVMs = () => {
    const filteredVMs = getFilteredValueMessages();
    const filteredVMIds = filteredVMs.map(vm => vm.id);
    setSelectedVMIds(prev => prev.filter(id => !filteredVMIds.includes(id)));
  };

  const handlePreviewDocument = (docId, documentName) => {
    setPreviewDocId(docId);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };
  
  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
  };

  // Filter function for value messages - Fixed filtering logic
  const getFilteredValueMessages = () => {
    let filtered = valueMessages || [];

    // Apply search filter
    if (vmSearch.trim()) {
      const searchTerm = vmSearch.toLowerCase();
      filtered = filtered.filter((vm) => {
        const messageMatch = (vm.value_message || "")
          .toLowerCase()
          .includes(searchTerm);
        const categoryMatch = (vm.type_name || "")
          .toLowerCase()
          .includes(searchTerm);
        const addedByMatch = (vm.added_by || "")
          .toLowerCase()
          .includes(searchTerm);
        const subTypesMatch = (vm.sub_types || []).some((subType) =>
          (subType || "").toLowerCase().includes(searchTerm)
        );
        const documentsMatch = (vm.documents || []).some((doc) =>
          (doc.document_name || "").toLowerCase().includes(searchTerm)
        );

        return messageMatch || categoryMatch || addedByMatch || subTypesMatch || documentsMatch;
      });
    }

    // Apply category filter
    if (vmCategory) {
      filtered = filtered.filter(vm => vm.type_name === vmCategory);
    }

    // Apply added by filter - Fixed logic
    if (vmAddedBy) {
      filtered = filtered.filter(vm => {
        if (vmAddedBy.toUpperCase() === 'HUMAN') {
          // For HUMAN filter, show items that are created by humans
          // This includes items with email addresses or where ai_human_generated is not "AI"
          return vm.ai_human_generated !== "AI" || (vm.created_by_email && vm.created_by_email.trim() !== '');
        } else if (vmAddedBy.toUpperCase() === 'AI') {
          // For AI filter, show items where ai_human_generated === "AI"
          return vm.ai_human_generated === "AI";
        }
        return true;
      });
    }

    // Sort so selected items appear first
    filtered.sort((a, b) => {
      const aSelected = selectedVMIds.includes(a.id) ? 0 : 1;
      const bSelected = selectedVMIds.includes(b.id) ? 0 : 1;
      return aSelected - bSelected;
    });

    return filtered;
  };

  // Paginate filtered results
  const getPaginatedValueMessages = () => {
    const filtered = getFilteredValueMessages();
    const startIndex = (vmPage - 1) * vmSize;
    const endIndex = startIndex + vmSize;
    return filtered.slice(startIndex, endIndex);
  };

  if (!isOpen) return null;

  const currentSection = sectionsDict[currentSectionForVM];
  const filteredVMs = getFilteredValueMessages();
  const paginatedVMs = getPaginatedValueMessages();
  const totalPages = Math.ceil(filteredVMs.length / vmSize);

  return (
    <>
      <div className="modal fade show d-block bg-dark bg-opacity-50">
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content ev-message-card2 d-block ev-card-3">
            {/* Header */}
            <div className="row mb-0 modal-header d-flex align-items-center border-0 ">
              <div className="col-7">
                <span className="ev-reg-p-blue mb-2">
                  <i className="bi bi-chat-square-text-fill"></i> &nbsp;
                   <TrimContent text={fabricName} maxLength={25} />
                  <span className="ev-message-card2 ms-2 ev-btn-small ev-radius-large">
                    Section {currentSection?.section_ordering_id}
                  </span>
                </span>
                <button 
                  type="button"
                  className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg ms-1"
                  onClick={selectAllVMs}
                >
                  Select All
                </button>
                <button 
                  type="button"
                  className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg ms-1"
                  onClick={deselectAllVMs}
                >
                  Deselect All
                </button>
                <span className="badge bg-primary  ev-fs-11 ms-2" >
                  {selectedVMIds.length} of {filteredVMs.length} selected
                </span>
              </div>
              <div className="col-4">
                {/* Search Bar */}
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control search-input ms-0"
                    placeholder="Search messages"
                    value={vmSearchInput}
                    onChange={handleVmSearchInputChange}
                    onKeyPress={handleVmKeyPress}
                  />
                  <button
                    className="ev-btn-lightblue-fill-round search-bar-button mt-0 top-50 start-0 translate-middle-y"
                    onClick={handleVmSearch}
                  >
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </div>
              <div className="col-1 text-end" >
                <button
                  type="button"
                  className="btn-close ev-btn-light-fill ev-fs-20 p-0 px-1 pb-2"
                  onClick={handleSaveSelection}
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>
            </div>

            {/* Table Container */}
            <div className="table-container">
              <table className="table-custom1 mt-0 overflow-hidden w-100">
                <thead>
                  <tr>
                    <th className="w-20">
                      <div className="d-flex">
                        <label className="ev-small-p-blue">Category </label>
                        <select
                          className="ev-message-card2 ps-2 pe-2 pt-1 pb-1 ev-btn-small ms-2 w-40"
                          value={vmCategory}
                          onChange={(e) => {
                            setVmCategory(e.target.value);
                            setVmPage(1);
                          }}
                        >
                          <option value="">All</option>
                          {categories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>
                    </th>
                    <th className="w-25">
                      <div className="d-flex">
                        <label className="ev-small-p-blue">Added By </label>
                        <select
                          className="ev-message-card2 ps-2 pe-2 pt-1 pb-1 ev-btn-small ms-2 w-40"
                          value={vmAddedBy}
                          onChange={(e) => {
                            setVmAddedBy(e.target.value);
                            setVmPage(1);
                          }}
                        >
                          <option value="">All</option>
                          {addedByOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    </th>
                    <th className="w-55">
                      <label className="ev-small-p-blue">Value Message</label>
                    </th>
                  </tr>
                </thead>
              </table>
              
              <div className="scrollable-element dashboard-right-contentarea-table-height-1">
                <table className="table-custom1 overflow-hidden w-100">
                  <tbody>
                    {vmLoading ? (
                      <div className="w-100 text-center">
                        <p className="ev-small-p-blue mt-5">Loading value messages</p>
                      </div>
                    ) : paginatedVMs.length === 0 ? (
                      <div className="w-100 text-center">
                        <p className="ev-small-p-blue mt-5">
                          {filteredVMs.length === 0 ? "No value messages found" : "No results for current page"}
                        </p>
                      </div>
                    ) : (
                      paginatedVMs.map((vm, index) => (
                        <tr
                          className="ev-card-2 cursor-pointer"
                          key={vm.id || index}
                          onClick={() => handleVMToggle(vm.id)}
                        >
                          <td className="align-top w-20">
                            <div className="d-flex">
                              <div className="form-check mt-1 me-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={selectedVMIds.includes(vm.id)}
                                  onChange={() => handleVMToggle(vm.id)}
                                  id={`vm_${vm.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="mb-2">
                                <p className="ev-reg-p-blue ms-1 mb-1">
                                  {vm.type_name}
                                </p>
                                {vm.sub_types &&
                                  vm.sub_types.map((subType, idx) => (
                                    <span
                                      key={idx}
                                      className="ev-message-card1 me-1 ev-btn-xsmall mt-1"
                                    >
                                      {subType}
                                    </span>
                                  ))}
                              </div>
                            </div>
                          </td>
                          <td className="align-top w-25">
                            <div className="mt-1">
                              {vm.ai_human_generated === "AI" ? (
                                <span className="badge gradient-bg ev-fs-11 ev-bold-reg">
                                  <i className="bi bi-stars"></i> Generated using AI 
                                </span>
                              ) : (
                                <span
                                  className="ev-message-card5 avatar-tooltip ev-fs-11"
                                  data-tooltip={vm.created_by_email || "Unknown User"}
                                >
                                  <TrimContent text={vm.created_by_email } maxLength={20} />
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="align-top w-55">
                            <ReadMoreTruncateText
                              text={vm.value_message}
                              limit={70}
                            />
                          <div className="mt-2">
                            <span className="ev-xsmall-p-blue">Referenced In</span>
                            <div className="flex mt-1">
                              {vm.documents && vm.documents.length > 0 ? (
                                <>
                                  {(expandedDocuments[vm.id] ? vm.documents : vm.documents.slice(0, 3)).map((doc, idx) => (
                                    <span
                                      key={idx}
                                      className="ev-message-card1 ev-fs-10 me-1 mb-1 pe-1"
                                    >
                                      {doc.document_name}
                                      <button
                                        className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg ms-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePreviewDocument(
                                            doc.document_id,
                                            doc.document_name
                                          );
                                        }}
                                      >
                                        <i className="bi bi-eye"></i> View
                                      </button>
                                    </span>
                                  ))}
                                  {vm.documents.length > 3 && (
                                    <>
                                      {!expandedDocuments[vm.id] && (
                                        <span className="ev-xsmall-p-blue me-1">
                                          + {vm.documents.length -  3} more
                                        </span>
                                      )}
                                      <button
                                        className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedDocuments(prev => ({
                                            ...prev,
                                            [vm.id]: !prev[vm.id]
                                          }));
                                        }}
                                      >
                                        {expandedDocuments[vm.id] ? 'Hide' : 'Show all'}
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : (
                                <span className="ev-small-p-dark">
                                  <i>No documents referenced </i>
                                </span>
                              )}
                            </div>
                          </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="modal-footer border-0" >
              {/* Pagination */}
              {filteredVMs.length > 0 && (
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div>
                    <span className="ev-small-p-dark">
                      Showing {paginatedVMs.length} of {filteredVMs.length} value messages
                      {vmSearch.trim() && <span> (filtered by "{vmSearch}")</span>}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="ev-btn-light-fill"
                      disabled={vmPage === 1}
                      onClick={() => setVmPage(vmPage - 1)}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                    <button className="ev-btn-light-fill" disabled>
                      Page {vmPage} of {Math.max(1, totalPages)}
                    </button>
                    <button
                      className="ev-btn-light-fill"
                      disabled={vmPage >= totalPages}
                      onClick={() => setVmPage(vmPage + 1)}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                      <PaginationControls
                        initialSize={vmSize}
                        onSizeChange={(newSize) => {
                          setVmSize(newSize);
                          setVmPage(1); // RESET PAGE TO 1
                        }}
                      />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      <DocumentPreview
        isOpen={previewOpen}
        onClose={closePreview}
        docId={previewDocId}
        documentName={previewDocumentName}
      />
    </>
  );
};

export default ValueMessageSelector;